import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import {
  CalendarOutlined,
  QuestionCircleOutlined,
  ReadOutlined,
  RightOutlined,
  ShareAltOutlined,
  TrophyOutlined,
} from "@ant-design/icons";
import { Tooltip } from "antd";
import lotteryWheelBg from "../../../assets/lottery-wheel-bg-icdesign.png";
import lotteryWheelFrame from "../../../assets/lottery-wheel-frame-icdesign.png";

function ActivityCountdownTimer({ endAt }) {
  const endDate = useMemo(() => {
    if (!endAt) return null;
    const normalizedEndAt = endAt.replace(" ", "T");
    return new Date(`${normalizedEndAt}+08:00`);
  }, [endAt]);
  const [timeRemaining, setTimeRemaining] = useState(
    endDate ? Math.max(endDate - new Date(), 0) : 0
  );

  useEffect(() => {
    if (!endDate) {
      setTimeRemaining(0);
      return undefined;
    }

    setTimeRemaining(Math.max(endDate - new Date(), 0));
    const interval = setInterval(() => {
      setTimeRemaining(Math.max(endDate - new Date(), 0));
    }, 1000);

    return () => clearInterval(interval);
  }, [endDate]);

  if (!endDate) return <span>活动时间加载中</span>;

  const days = Math.floor(timeRemaining / (1000 * 60 * 60 * 24));
  const hours = Math.floor(
    (timeRemaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutes = Math.floor(
    (timeRemaining % (1000 * 60 * 60)) / (1000 * 60)
  );
  const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
  const formatTime = (time) => String(time).padStart(2, "0");

  if (timeRemaining <= 0) return <span>活动已结束</span>;

  return (
    <span>
      活动倒计时：{days}天 {formatTime(hours)}:{formatTime(minutes)}:
      {formatTime(seconds)}
    </span>
  );
}

function LotteryWheel({
  rewards,
  rotation,
  segmentAngle,
  drawing,
  chances,
  isLoggedIn,
  rightsLevel,
  onDraw,
}) {
  const ruleText = isLoggedIn
    ? `今日剩余 ${chances} 次。权益等级 Lv ${rightsLevel || 0} 每天免费 ${
        rightsLevel || 0
      } 次，完成每日任务可额外获得抽奖次数。`
    : "登录后按个人权益等级发放每日免费抽奖次数，完成每日任务可额外获得抽奖次数。";

  return (
    <div className="lottery-wheel-area">
      <img
        className="lottery-wheel-bg-image"
        src={lotteryWheelBg}
        alt=""
        draggable={false}
      />
      <Tooltip title={ruleText} placement="left">
        <button type="button" className="lottery-mobile-rule-trigger">
          <QuestionCircleOutlined />
          <span>规则介绍</span>
        </button>
      </Tooltip>
      <div className="lottery-decor lottery-decor-a">券</div>
      <div className="lottery-decor lottery-decor-b">分</div>
      <div className="lottery-decor lottery-decor-c">礼</div>
      <div className="lottery-pointer"></div>
      <div
        className="lottery-wheel"
        style={{
          transform: `rotate(${rotation}deg)`,
        }}
      >
        <img
          className="lottery-wheel-frame-image"
          src={lotteryWheelFrame}
          alt=""
          draggable={false}
        />
        {rewards.map((reward, index) => {
          const rewardAngle = index * segmentAngle + segmentAngle / 2;

          return (
            <div
              className="lottery-wheel-reward"
              key={reward.id || `${reward.label}-${index}`}
              style={{
                transform: `rotate(${rewardAngle}deg)`,
              }}
            >
              <div
                className={`lottery-wheel-reward-content${
                  reward.tier === "grand" ? " lottery-wheel-reward-grand" : ""
                }`}
                style={{
                  transform: `translate(-50%, var(--lottery-reward-distance, -126px)) rotate(-${
                    rewardAngle + rotation
                  }deg)`,
                }}
              >
                <span className="lottery-wheel-reward-icon">{reward.icon}</span>
                <span className="lottery-wheel-reward-label">
                  {reward.label}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <button
        className="lottery-wheel-button"
        disabled={drawing || (isLoggedIn && chances <= 0)}
        onClick={onDraw}
      >
        抽奖
      </button>
    </div>
  );
}

function LotteryTaskList({ sharePosterLoading, onOpenSharePoster }) {
  const history = useHistory();
  const taskList = [
    {
      icon: <ReadOutlined />,
      title: "题库发帖",
      reward: "额外 +10 权益积分",
      action: "去发帖",
      link: "/recruitment-question-set",
    },
    {
      icon: <ShareAltOutlined />,
      title: "用户分享",
      reward: "每日 +1 次抽奖机会",
      action: sharePosterLoading ? "生成中" : "去分享",
      onClick: onOpenSharePoster,
    },
  ];
  const handleTaskClick = (task) => {
    if (task.disabled || (task.onClick && sharePosterLoading)) return;
    if (task.onClick) {
      task.onClick();
      return;
    }
    if (task.link) history.push(task.link);
  };

  return (
    <div className="lottery-task-panel">
      <div className="lottery-task-title">每日任务</div>
      <div className="lottery-task-list">
        {taskList.map((task) => (
          <div
            className={`lottery-task-item${
              task.disabled ? " lottery-task-item-disabled" : ""
            }${task.mobileHidden ? " lottery-task-item-mobile-hidden" : ""}`}
            key={task.title}
            onClick={() => handleTaskClick(task)}
            onKeyDown={(event) => {
              if (event.key === "Enter") handleTaskClick(task);
            }}
            role="button"
            tabIndex={0}
          >
            <div className="lottery-task-icon">{task.icon}</div>
            <div className="lottery-task-content">
              <div className="lottery-task-name">{task.title}</div>
              <div className="lottery-task-reward">{task.reward}</div>
            </div>
            {task.disabled ? (
              <span className="lottery-task-button lottery-task-button-disabled">
                {task.action}
              </span>
            ) : task.onClick ? (
              <button
                type="button"
                className="lottery-task-button"
                onClick={(event) => {
                  event.stopPropagation();
                  task.onClick();
                }}
                disabled={sharePosterLoading}
              >
                {task.action}
              </button>
            ) : (
              <NavLink
                className="lottery-task-button"
                to={task.link}
                onClick={(event) => event.stopPropagation()}
              >
                {task.action}
              </NavLink>
            )}
          </div>
        ))}
      </div>
      <NavLink
        className="lottery-prize-link"
        to="/user/center/my-coupons?tab=lottery_records"
      >
        查看我的奖品
        <RightOutlined />
      </NavLink>
    </div>
  );
}

function LotteryPanel({
  activityEndAt,
  chances,
  drawing,
  isLoggedIn,
  rewards,
  rightsLevel,
  rotation,
  segmentAngle,
  sharePosterLoading,
  onDraw,
  onOpenSharePoster,
}) {
  const grandPrizeText = "50权益积分,直播抵扣券20元,专栏5折券";

  return (
    <div className="lottery-panel">
      <div className="lottery-info">
        <div className="lottery-title">每日抽奖活动</div>
        <div className="lottery-subtitle">权益等级越高，每天可抽次数越多。</div>
        <div className="lottery-rights-card">
          <div className="lottery-rights-card-label">当前权益规则</div>
          <div className="lottery-rights-card-value">
            {rightsLevel ? (
              <>
                <span>Lv {rightsLevel}</span>
                <b>=</b>
                <span>每日 {rightsLevel} 次免费抽奖</span>
              </>
            ) : (
              <span>登录后查看每日免费抽奖次数</span>
            )}
          </div>
          <div className="lottery-rights-card-mobile-summary">
            <div className="lottery-rights-card-mobile-main">
              <span>今日剩余</span>
              <b>{isLoggedIn ? `${chances} 次` : "登录查看"}</b>
            </div>
            <div className="lottery-rights-card-mobile-rule">
              {rightsLevel
                ? `Lv ${rightsLevel} = 每日 ${rightsLevel} 次免费抽奖`
                : "登录后按权益等级发放每日免费次数"}
            </div>
          </div>
          <NavLink
            className="lottery-rights-card-link"
            to="/user/center/my-rights"
          >
            查看我的权益
            <RightOutlined />
          </NavLink>
        </div>
        <div className="lottery-feature-list">
          <div className="lottery-feature-item">
            <TrophyOutlined />
            <span>大奖：{grandPrizeText}</span>
          </div>
          <div className="lottery-feature-item">
            <CalendarOutlined />
            <ActivityCountdownTimer endAt={activityEndAt} />
          </div>
        </div>
        <button
          className="lottery-primary-button"
          disabled={drawing || (isLoggedIn && chances <= 0)}
          onClick={onDraw}
        >
          {isLoggedIn
            ? chances > 0
              ? `立即抽奖（剩余 ${chances} 次）`
              : "次数已用完"
            : "立即抽奖"}
          <RightOutlined />
        </button>
      </div>
      <LotteryWheel
        rewards={rewards}
        rotation={rotation}
        segmentAngle={segmentAngle}
        drawing={drawing}
        chances={chances}
        isLoggedIn={isLoggedIn}
        rightsLevel={rightsLevel}
        onDraw={onDraw}
      />
      <LotteryTaskList
        sharePosterLoading={sharePosterLoading}
        onOpenSharePoster={onOpenSharePoster}
      />
    </div>
  );
}

export default LotteryPanel;
